import produce from 'immer';
import createCachedSelector from 're-reselect';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { getUtcNow } from '../../Components/AgentsTable';
import { ConversationFlags, InstantMessageConversation, InstantMessagerJoinState, IsQueueConversation } from '../../model';
import { stripHtmlTags } from '../../phone-utils';
import { MessageType } from '../../uc-messaging';
import { Actions, StoreState } from '../types';


interface InstantMessagerById {
  [id: string]: InstantMessageConversation
}

function instantMessagerById(state: InstantMessagerById = {}, action: Actions): InstantMessagerById {
  switch (action.type) {
    case getType(actions.instantMessagerDataResponse): {
      //might have had some loading with messages
      return produce(state, state => {

        action.payload.forEach(conversation => {
          const c = state[conversation.conversationId]
          if (!c) {
            state[conversation.conversationId] = { ...conversation, lastUnreadMessage: stripHtmlTags(conversation.lastUnreadMessage) }
          } else {
            c.conversationId = conversation.conversationId
            c.conversationTitle = conversation.conversationTitle
            c.createdUtc = conversation.createdUtc
            c.lastUpdatedUtc = conversation.lastUpdatedUtc
            c.conversationFlags = conversation.conversationFlags ?? ConversationFlags.None
            c.deviceEndpoint = conversation.deviceEndpoint
            c.unreadMessagesTotal = conversation.unreadMessagesTotal
            c.unreadUrgentMessagesTotal = conversation.unreadUrgentMessagesTotal
            c.lastUnreadMessage = stripHtmlTags(conversation.lastUnreadMessage)
            c.removedFromConversationTitle = conversation.removedFromConversationTitle
            c.removedFromConversationTimestampUtc = conversation.removedFromConversationTimestampUtc
          }
        });
      })
    }
    case getType(actions.setInstantMessagerJoinConversation): {
      const { conversationId, joiningState, create } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const conversation = state[id]
      if (conversation) {
        return { ...state, [id]: { ...conversation, joined: joiningState, conversationId: id } }
      }

      if (create && !conversation) {
        return {
          ...state, [id]:
          {
            conversationId: id,
            conversationTitle: '',
            conversationFlags: ConversationFlags.None,
            deviceEndpoint: '',
            unreadMessagesTotal: 0,
            unreadUrgentMessagesTotal: 0,
            lastUnreadMessage: '',
            removedFromConversationTitle: '',
            participants: [],
            joined: joiningState,
            recipients: [],
            createdStoreDateUtc: getUtcNow(),
          },
        }
      }

      return state
    }
    case getType(actions.instantMessagerConversationTitleUpdate): {
      const { conversationId, title } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const conversation = state[id]
      if (conversation) {
        return { ...state, [id]: { ...conversation, conversationTitle: title } }
      }
      return state
    }
    case getType(actions.instantMessagerConversationOneToOneUpdate): {
      const { conversationId, oneToOne } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const conversation = state[id]
      if (conversation) {
        return { ...state, [id]: { ...conversation, oneToOneConversation: oneToOne } }
      }
      return state
    }

    case getType(actions.requestLeaveConversation): {
      return produce(state, state => {
        const c = state[action.payload.toLocaleLowerCase()]
        if (c) {
          c.messages = []
        }
      })
    }
    case getType(actions.removeMediaItem): {
      const id = action.payload.toLocaleLowerCase()
      const im = state[id]
      if (!im) { return state }

      const { [id]: _, ...rest } = state
      return rest
    }
    case getType(actions.instantMessagerConversationUpdate): {
      //there is a lot of undefined data coming in from the 3 different messages so going ot massage it a little
      return produce(state, state => {
        const { messageId, conversation } = action.payload
        const id = conversation.conversationId.toLocaleLowerCase()
        const c = state[id]
        if (!c) {
          state[id] = { ...conversation, conversationId: id, lastUnreadMessage: stripHtmlTags(conversation.lastUnreadMessage) }
        } else {
          c.conversationId = id
          c.conversationFlags = conversation.conversationFlags
          if (conversation.conversationTitle !== undefined)
            c.conversationTitle = conversation.conversationTitle
          if (conversation.createdUtc !== undefined)
            c.createdUtc = conversation.createdUtc
          if (conversation.lastUpdatedUtc !== undefined)
            c.lastUpdatedUtc = conversation.lastUpdatedUtc
          if (conversation.deviceEndpoint !== undefined)
            c.deviceEndpoint = conversation.deviceEndpoint
          if (conversation.unreadMessagesTotal !== undefined)
            c.unreadMessagesTotal = conversation.unreadMessagesTotal
          if (conversation.unreadUrgentMessagesTotal !== undefined)
            c.unreadUrgentMessagesTotal = conversation.unreadUrgentMessagesTotal
          if (conversation.lastUnreadMessage !== undefined)
            c.lastUnreadMessage = stripHtmlTags(conversation.lastUnreadMessage)
          if (conversation.removedFromConversationTitle !== undefined)
            c.removedFromConversationTitle = conversation.removedFromConversationTitle
          if (conversation.removedFromConversationTimestampUtc !== undefined)
            c.removedFromConversationTimestampUtc = conversation.removedFromConversationTimestampUtc
          if (conversation.currentUserId !== undefined)
            c.currentUserId = conversation.currentUserId
          if (conversation.oneToOneConversation !== undefined)
            c.oneToOneConversation = conversation.oneToOneConversation
          if (conversation.conversationViewOnly !== undefined)
            c.conversationViewOnly = conversation.conversationViewOnly

          if (messageId === MessageType.BROADCAST_IM_CONVERSATIONJOIN) {
            c.joined = InstantMessagerJoinState.joined
            c.messages = []
            c.participants = []
            for (let index = 0; index < conversation.participants.length; index++) {
              const element = conversation.participants[index];
              const i = c.participants.findIndex(p => p.participantId === element.participantId)
              if (i !== -1) {
                c.participants[i].isCurrentUser = element.isCurrentUser
                c.participants[i].displayName = element.displayName
                c.participants[i].participantId = element.participantId
                c.participants[i].sendInviteMessagesOnly = element.sendInviteMessagesOnly
                c.participants[i].state = element.state
                c.participants[i].status = element.status
                c.participants[i].typing = false
              } else {
                c.participants.push({
                  participantId: element.participantId,
                  endpointScheme: element.endpointScheme,
                  isCurrentUser: element.isCurrentUser,
                  endpointId: element.endpointId,
                  endpointServer: element.endpointServer,
                  displayName: element.displayName,
                  sendInviteMessagesOnly: element.sendInviteMessagesOnly,
                  status: element.status,
                  state: element.state,
                  typing: false,
                })
              }
            }
          } else {
            if (conversation.recipients !== undefined) {
              c.recipients = conversation.recipients
            }
          }
        }
      })
    }
    case getType(actions.instantMessagerConversationEndpointRemove): {
      const newState = { ...state }
      delete newState[action.payload]
      return newState
    }
    case getType(actions.instantMessagerMessageAdd): {
      const { conversationId, messageId, message } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const conversation = state[id]
      const x = message.replace(/txt-item left/g, 'txt-item left-item').replace(/txt-item right/g, 'txt-item right-item')
      const m = { messageId, message: x, createdUtc: getUtcNow() }


      if (conversation) {
        return { ...state, [id]: { ...conversation, messages: !conversation.messages ? [m] : [...conversation.messages, m] } }
      }
      return state
    }
    case getType(actions.instantMessagerMessageReplace): {
      return produce(state, state => {
        const { conversationId, messageId, message } = action.payload
        const id = conversationId.toLocaleLowerCase()
        const x = message.replace(/txt-item left/g, 'txt-item left-item').replace(/txt-item right/g, 'txt-item right-item')
        const conversation = state[id]

        if (!conversation) {
          return
        }

        if (!conversation.messages) {
          conversation.messages = [{ messageId, message: x }]
          return
        }

        const m = conversation.messages.find(m => m.messageId === messageId)
        if (!m) {
          conversation.messages = [...conversation.messages, { messageId, message: x }]
        } else {
          m.message = x
        }
      })
    }
    case getType(actions.instantMessagerParticipantTyping): {
      return produce(state, state => {
        const { conversationId, participantId, typing } = action.payload
        const id = conversationId.toLocaleLowerCase()
        const conversation = state[id]

        if (!conversation) {
          return
        }
        const participant = conversation.participants.find(p => p.participantId === participantId)
        if (!participant) {
          return
        }
        participant.typing = typing
      })
    }
    case getType(actions.instantMessagerParticipantUpdate): {
      return produce(state, state => {
        const { conversationId, participantId, endpointScheme, endpointId, endpointServer, displayName, status, sendInviteMessagesOnly, isCurrentUser } = action.payload
        const id = conversationId.toLocaleLowerCase()
        const conversation = state[id]

        if (!conversation) {
          return
        }
        //we have a mix of recipient data and particiapnt data as a participant
        // recipient data doesn't have participantId
        const participant = conversation.participants.find(p => p.participantId === participantId || (p.endpointScheme === endpointScheme && p.endpointServer === endpointServer && p.endpointId === endpointId))
        if (!participant) {
          conversation.participants.push({ participantId, endpointScheme, isCurrentUser: isCurrentUser, endpointId, endpointServer, displayName, sendInviteMessagesOnly, status, state: action.payload.state, typing: false })
        } else {
          participant.isCurrentUser = isCurrentUser
          participant.displayName = displayName
          participant.participantId = participantId
          participant.sendInviteMessagesOnly = sendInviteMessagesOnly
          participant.state = action.payload.state
          participant.status = status
          participant.typing = false
        }
      })
    }
    case getType(actions.instantMessagerMessageHistory): {
      return produce(state, state => {
        const { conversationId, messageId, messages } = action.payload
        const id = conversationId.toLocaleLowerCase()
        const conversation = state[id]
        if (!conversation) {
          return
        }
        if (!conversation.messages) {
          conversation.messages = []
        }

        if (messageId === '') {
          conversation.messages = conversation.messages ? messages.concat(conversation.messages) : messages
        } else {
          //history is added with out message ids
          const index = conversation.messages.findIndex(m => m.message.lastIndexOf(messageId) !== -1)
          if (index > -1) {
            if (conversation.messages) {
              for (let x = 0; x < messages.length; x++) {
                const message = messages[x];
                if (conversation.messages) {
                  conversation.messages.splice(index + 1 + x, 0, message)
                }
              }
            }
          }
        }
      })
    }
    case getType(actions.instantMessagerParticipantRemove): {
      const { conversationId, participantId } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const conversation = state[id]
      if (conversation && conversation.participants) {
        return { ...state, [id]: { ...conversation, participants: conversation.participants.filter(p => p.participantId !== participantId) } }
      }
      return state
    }
    case getType(actions.forceReload): {
      return {}
    }
  }

  return state
}

function itemsLoaded(state: boolean = false, action: Actions): boolean {
  switch (action.type) {
    case getType(actions.instantMessagerDataResponse): {
      state = true
      return state
    }
    case getType(actions.forceReload): {
      return false
    }
  }

  return state
}

export interface InstantMessagerNotification {
  conversationId: string
  unread: number //total count
  urgent: boolean
  message: string
  title: string
  blocked: boolean
  removing: boolean
}

const empty = {
  conversationId: '',
  unread: 0,
  urgent: false,
  message: '',
  title: '',
  blocked: false,
  removing: false,
}

function notification(state: InstantMessagerNotification = empty, action: Actions): InstantMessagerNotification {
  switch (action.type) {

    case getType(actions.instantMessagerToaster): {
      const { conversationId, unread, urgent, message, title, blocked } = action.payload
      const id = conversationId.toLocaleLowerCase()
      const normal = state.urgent
      return {
        conversationId: id, unread, urgent, message: stripHtmlTags(message), title, blocked, removing: normal > urgent,
      }
    }

    case getType(actions.instantMessageNotifcationHide):
      return {
        ...state, conversationId: '',
      }

    case getType(actions.shutdown):
      return empty

  }

  return state
}

interface InstantMessagerCounts {
  normal: number
  urgent: number
}

function counts(state: InstantMessagerCounts = { normal: 0, urgent: 0 } as InstantMessagerCounts, action: Actions): InstantMessagerCounts {
  switch (action.type) {
    case getType(actions.instantMessagerUnreadCount):
      {
        const { unreadNormal, unreadUrgent } = action.payload
        return { normal: unreadNormal, urgent: unreadUrgent }
      }
  }
  return state
}

function urgent(state: boolean = false, action: Actions): boolean {
  switch (action.type) {
    case getType(actions.setInstantMessageUrgent): {
      const { value } = action.payload
      return value === undefined ? !state : value
    }
  }

  return state
}

export default combineReducers({
  instantMessagerById,
  itemsLoaded,
  notification,
  counts,
  urgent,
})

function getRoot(state: StoreState) { return state.instantMessager }
function getInstantMessagerById(state: StoreState) { return getRoot(state).instantMessagerById }
function getItemsLoaded(state: StoreState) { return getRoot(state).itemsLoaded }
function getInstantMessagerNotification(state: StoreState) { return getRoot(state).notification }
function getInstantMessagerCounts(state: StoreState) { return getRoot(state).counts }
function getInstantMessagerUrgent(state: StoreState) { return getRoot(state).urgent }

export function getInstantMessageLoaded(state: StoreState) {
  return getItemsLoaded(state)
}

export function getInstantMessageById(state: StoreState, conversationId: string) {
  const id = conversationId.toLocaleLowerCase()
  return getInstantMessagerById(state)[id]
}

export function getInstantMessageNotification(state: StoreState) {
  return getInstantMessagerNotification(state)
}

export function getInstantMessageCounts(state: StoreState) {
  return getInstantMessagerCounts(state)
}

export function getInstantMessageUrgent(state: StoreState) {
  return getInstantMessagerUrgent(state)
}

export const selectInstantMessagerJoined = createCachedSelector(
  getInstantMessagerById,
  (s: StoreState, conversationId: string) => conversationId,
  (ims, conversationId) => {
    if (!ims) return false
    const id = conversationId.toLocaleLowerCase()
    const i = ims[id]
    if (!i) return false

    return i.joined === InstantMessagerJoinState.joined
  },
)(
  (s, conversationId) => conversationId.toLocaleLowerCase(),
)

export const selectAllMessages = createSelector(
  getInstantMessagerById,
  conversations => conversations ? Object.values(conversations).filter(c => {
    if (c.conversationFlags) {
      return !IsQueueConversation(c)
    }
    return true
  }).sort((a, b) => a.createdStoreDateUtc && b.createdStoreDateUtc && a.createdStoreDateUtc < b.createdStoreDateUtc ? 1 : -1) : undefined,
)
